import React, { useState, useEffect } from 'react';
import { WidgetContainer } from '@frontend/app/refresh-components';
import { cn } from '@frontend/shadcn/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { useGetBudgetDashboardProjectTabSummaryQuery } from '@frontend/app/hooks';
import { useGetBudgetFiscalPeriod } from '@frontend/app/hooks/budgetAllocation/useGetBudgetFiscalPeriod';
import { TBudgetFiscalPeriod } from '@frontend/applications/PaymentsApp/types';
import { Select } from '@revfluence/fresh';
import { SackDollarIcon } from '@revfluence/fresh-icons/solid/esm';
import { TotalBudgetCard } from './TotalBudgetCard';
import { TotalAllocatedToBriefsCard } from './TotalAllocatedToBriefsCard';
import { TotalSpentCard } from './TotalSpentCard';

const { Option } = Select;

interface BudgetProps {
  projectId?: number;
  className?: string;
}

export default function Budget(props: BudgetProps) {
  const { projectId, className } = props;
  const [financialYear, setFinancialYear] = useState<TBudgetFiscalPeriod[]>([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState<TBudgetFiscalPeriod | null>(null);

  const { loading: budgetFiscalPeriodLoading, budgetFiscalPeriod } = useGetBudgetFiscalPeriod({});

  const { data, loading, error } = useGetBudgetDashboardProjectTabSummaryQuery({
    variables: {
      programId: projectId,
      fiscalYear: selectedFinancialYear?.fiscalYear || '',
    },
    skip: !projectId || !selectedFinancialYear?.fiscalYear,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!budgetFiscalPeriodLoading && budgetFiscalPeriod.length) {
      setFinancialYear(budgetFiscalPeriod);
      setSelectedFinancialYear(budgetFiscalPeriod.find((period) => period.isCurrentYear) || budgetFiscalPeriod[0] || null);
    }
  }, [budgetFiscalPeriodLoading, budgetFiscalPeriod]);

  const budgetData = data?.summary;
  const isLoading = loading || budgetFiscalPeriodLoading;

  if (error) {
    return (
      <WidgetContainer
        widgetTitle="Budget"
        textColor="text-grey-6 text-sm"
        bgColor="bg-white"
        widgetIcon={<FontAwesomeIcon icon={faCreditCard} className="text-grey-4" />}
        widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
        className={cn('h-min', className)}
      >
        <div className="p-4 text-center text-red-500">
          Error loading budget data
        </div>
      </WidgetContainer>
    );
  }

  const isParametersAbsent = !projectId || !selectedFinancialYear?.fiscalYear;

  if (isParametersAbsent) {
    return (
      <WidgetContainer
        widgetTitle="Budget"
        textColor="text-grey-6 text-sm"
        bgColor="bg-white"
        widgetIcon={<FontAwesomeIcon icon={faCreditCard} className="text-grey-4" />}
        widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
        className={cn('h-min', className)}
      >
        <div className="p-4 text-center">
          <p>No data found</p>
        </div>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer
      widgetTitle="Budget"
      textColor="text-grey-6 text-sm"
      bgColor="bg-white"
      widgetIcon={<SackDollarIcon className="w-4 h-4 text-grey-4" />}
      widgetActions={
        <div className="flex items-center gap-2">
          {financialYear.length > 1 && selectedFinancialYear && (
            <Select
              value={selectedFinancialYear?.fiscalYear ?? ''}
              style={{ width: 120 }}
              onChange={(e) => setSelectedFinancialYear(financialYear.find((fy) => fy.fiscalYear === e))}
            >
              {financialYear.map((year) => (
                <Option key={year.fiscalYear} value={year.fiscalYear}>
                  {year.fiscalYearLabel}
                </Option>
              ))}
            </Select>
          )}
          <FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />
        </div>
      }
      className={cn('h-min', className)}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3">
        {/* Total Budget Card */}
        <div className="h-full">
          <TotalBudgetCard budgetData={budgetData} isLoading={isLoading} />
        </div>

        {/* Total Allocated to Briefs Card */}
        <div className="h-full">
          <TotalAllocatedToBriefsCard budgetData={budgetData} isLoading={isLoading} />
        </div>

        {/* Total Spent Card */}
        <div className="h-full">
          <TotalSpentCard budgetData={budgetData} isLoading={isLoading} />
        </div>
      </div>
    </WidgetContainer>
  );
}
